import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import DataOption from '../../models/DataOption';
import StatMessages from '../../StatMessages';
import ChartOption from '../../models/ChartOption';
import getHoursOffset from '../../../utils/getHoursOffset';

const messages = defineMessages({
  CALL_QUEUES: {
    id: 'CALL_QUEUES',
    defaultMessage: 'Call Queues',
  },
  GAU: {
    id: 'GAU',
    defaultMessage: 'GAU',
  },
});

export default class Callqueue {
  constructor(intl) {
    if (!intl) {
      throw Error('Missing intl param');
    }

    const hourOffset = getHoursOffset();

    /**
     * Default callqueuereport api params
     * @type {{object: string, action: string, format: string, range_interval: string, value_offset: string}}
     */
    const apiParams = {
      object: 'callqueue',
      action: 'read',
      format: 'json',
      range_interval: hourOffset,
      value_offset: hourOffset,
      run_stats: 'yes',
      unit: 'seconds',
    };

    const statMessages = new StatMessages(intl);

    /**
     * add socket subscriptions and events if any
     * Example Props
     * stat: 'AC'
     * socketSubscriptions: [{ type: 'agent' }]
     * socketEvents: [{ name: string, adapter: function, adapterParams: object }] }
     * @type {*[]}
     */
    const supportedColumns = [
      // { stat: 'QueueName', ...statMessages.messages.QueueName }, included as a default so its not included in the selectable columns
      // {stat: 'QueueExtension', ...statMessages.messages.QueueExtension}, //Removed as option do too CHRT-228

      // From the API, historical stats
      { stat: 'AC', ...statMessages.messages.AC },
      { stat: 'AAC', ...statMessages.messages.AAC },
      { stat: 'AAR', ...statMessages.messages.AAR },
      // { stat: 'AAS', ...statMessages.messages.AAS },
      { stat: 'ACO', ...statMessages.messages.ACO },
      { stat: 'AH', ...statMessages.messages.AH },
      { stat: 'AHT', ...statMessages.messages.AHT },
      { stat: 'AR', ...statMessages.messages.AR },
      { stat: 'ATT', ...statMessages.messages.ATT },
      { stat: 'AWT', ...statMessages.messages.AWT },
      { stat: 'CH', ...statMessages.messages.CH },
      { stat: 'CO', ...statMessages.messages.CO },
      { stat: 'DT', ...statMessages.messages.DT },
      { stat: 'FWD', ...statMessages.messages.FWD },
      { stat: 'SL', ...statMessages.messages.SL },
      { stat: 'TT', ...statMessages.messages.TT },
      { stat: 'VM', ...statMessages.messages.VM },
      { stat: 'VOL', ...statMessages.messages.VOL },
      { stat: 'AST', ...statMessages.messages.AST },
      { stat: 'CB', ...statMessages.messages.CB },
      { stat: 'SMS_VOL', ...statMessages.messages.SMS_VOL },
      { stat: 'SMS_AHT', ...statMessages.messages.SMS_AHT },

      // Live socket updated data
      { stat: 'ActiveCalls', ...statMessages.messages.ActiveCalls },
      { stat: 'CallsWaiting', ...statMessages.messages.CallsWaiting },
      { stat: 'AgentsAvailable', ...statMessages.messages.AgentsAvailable },
      { stat: 'AgentsLoggedIn', ...statMessages.messages.AgentsLoggedIn },
      { stat: 'LongestWait', ...statMessages.messages.LongestWait },
    ];

    /**
     * Displays chart options to the user
     * type: timeRange displays an input and sets the start/end for the api request
     * type: queueFilter displays an input and sets the queue/s for the api request
     */
    const chartOptions = [
      new ChartOption({
        type: 'columnList',
        required: true,
        columns: supportedColumns.map((column) => column.stat),
      }),
    ];

    this.dataOptions = new DataOption({
      type: 'api',
      apiParams,
      adapter: 'callQueuesToTableAdapter',
      baseColumns: ['QueueName'],
      chartOptions,
      defaultColumns: ['CallsWaiting', 'LongestWait', 'AgentsAvailable', 'VOL', 'CH'],
      filterCategories: ['callqueue'],
      hasCategories: false,
      hasColumns: true,
      label: <FormattedMessage {...messages.CALL_QUEUES} />,
      labelStr: intl.formatMessage({ ...messages.CALL_QUEUES }),
      socketEvents: [{ name: 'queue', adapter: 'queueSocketToCallQueuesTableAdapter' }],
      socketSubscriptions: [{ type: 'queue', unit: 'seconds' }],
      stat: 'Callqueue',
      supportedCharts: ['ResponsiveTable'],
      supportedColumns,
    });
  }
}
